"use client"
import { TransitionLink } from '@/components/TransitionLink';
import { useAuth } from '@/context/AuthProvider';
import useChatWebSocket from '@/hooks/useChatWebSocket';
import { MessageCircle, Phone } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SellerSection = ({ sallerInfo }) => {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const token = getToken();
  const i18nextLng = typeof window !== 'undefined' ? window.localStorage.getItem("i18nextLng") : 'en';
  const { sendMessage } = useChatWebSocket(token ? token : null, sallerInfo?.username)
  const [state, setState] = useState({
    followLoading: false,
    blockLoading: false,
    isMenuOpen: false,
    rooms: [],
    sallerStatus: {},
    showBlockDialog: false,
    showReportDialog: false,
    reportDescription: '',
    chatModel: false,
    chatMessage: ""
  });

  const router = useRouter();

  const fetchSallerInfo = useCallback(async () => {
    if (!token) return;

    try {
      // Use static data instead of API call
      const { staticSellerStatus } = await import('@/data/staticData');
      setState(prev => ({ ...prev, sallerStatus: staticSellerStatus }));
    } catch (error) {
      toast.error(error.message);
    }
  }, [sallerInfo?.username, token]);

  const fetchRooms = useCallback(async () => {
    if (!token) return;

    try {
      // Use static data instead of API call
      const { staticRooms } = await import('@/data/staticData');
      setState(prev => ({ ...prev, rooms: staticRooms || [] }));
    } catch (error) {
      console.error('Fetch rooms error:', error);
    }
  }, [token]);

  const toggleFollowStatus = useCallback(async () => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }

    setState(prev => ({ ...prev, followLoading: true }));
      
    try { 
      const endpoint = state.sallerStatus.followed
        ? `${process.env.NEXT_PUBLIC_BASE_URL}/auth/unfollow-account/${sallerInfo?.username}`
        : `${process.env.NEXT_PUBLIC_BASE_URL}/auth/follow-account/${sallerInfo?.username}`;

      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchSallerInfo(); // Refetch seller info after toggle

      setState(prev => ({
        ...prev,
        followLoading: false
      }));

      toast.success(state.sallerStatus.followed ? t("sallerInfo.success.unfollowed") : t("sallerInfo.success.followed"));
    } catch (error) {
      toast.error(error.message);
      setState(prev => ({ ...prev, followLoading: false }));
    }
  }, [sallerInfo?.username, token, t, fetchSallerInfo, state.sallerStatus.followed]);

  const toggleBlockStatus = useCallback(async () => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }

    setState(prev => ({ ...prev, blockLoading: true }));
    try {
      const endpoint = state.sallerStatus.blocked
        ? `${process.env.NEXT_PUBLIC_BASE_URL}/auth/unban-account/${sallerInfo?.username}`
        : `${process.env.NEXT_PUBLIC_BASE_URL}/auth/ban-account/${sallerInfo?.username}`;

      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchSallerInfo(); // Refetch seller info after toggle

      setState(prev => ({
        ...prev,
        blockLoading: false,
        showBlockDialog: false
      }));

      toast.success(state.sallerStatus.blocked ? t("User Unblocked Successfully") : t("User Blocked Successfully"));
    } catch (error) {
      toast.error(error.message);
      setState(prev => ({ ...prev, blockLoading: false }));
    }
  }, [sallerInfo?.username, token, t, fetchSallerInfo, state.sallerStatus.blocked]);

  const handleReportSubmit = useCallback(async () => {
    const { reportDescription } = state;
    if (!reportDescription.trim()) {
      toast.error(t("Please provide a description"));
      return;
    }

    try {
      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));

      await fetchSallerInfo(); // Refetch seller info after report

      setState(prev => ({
        ...prev,
        reportDescription: '',
        showReportDialog: false
      }));

      toast.success(t("Report submitted successfully"));
    } catch (error) {
      toast.error(error.message);
    }
  }, [sallerInfo?.username, token, state.reportDescription, t, fetchSallerInfo]);

  const chatHandler = useCallback(() => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }
    const existingRoom = state.rooms.some(
      (item) =>
        item.room_name.toLowerCase().includes(user?.username?.toLowerCase() || '') &&
        item.room_name.toLowerCase().includes(sallerInfo?.username?.toLowerCase() || '')
    );

    if (existingRoom) {
      router.push(`/Desktop/chat`);
    } else {
      setState(prev => ({ ...prev, chatModel: true }));
    }
  }, [state.rooms, user?.username, sallerInfo?.username, router]);

  useEffect(() => {
    fetchSallerInfo();
    fetchRooms();
  }, [fetchSallerInfo, fetchRooms]);

  useEffect(() => {
    const handleClickOutside = () => {
      setState(prev => ({ ...prev, isMenuOpen: false }));
    };

    if (state.isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [state.isMenuOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setState(prev => ({ ...prev, isMenuOpen: !prev.isMenuOpen }));
  };

  const handleSendMessage = () => {
    const message = state.chatMessage.trim()
    if (message) {
      sendMessage(message);
      setState(prev => ({ ...prev, chatMessage: "", chatModel: false })); // Close chat modal after sending
      router.push("/Mobile/oldChat")
    }
  }
  const renderActionDialogs = () => (
    <>
      {state.showBlockDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {state?.sallerStatus?.blocked ? t("Unblock User") : t("Block User")}
            </h3>
            <p className="text-gray-600 mb-4">
              {state?.sallerStatus?.blocked
                ? t("Are you sure you want to unblock this user?")
                : t("Are you sure you want to block this user?")}
            </p>
            <div className="flex justify-start gap-2">
              <button
                onClick={toggleBlockStatus}
                disabled={state.blockLoading}
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
              >
                {t("Confirm")}
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, showBlockDialog: false }))}
                disabled={state.blockLoading}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {state.showReportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t("Report User")}</h3>
            <textarea
              value={state.reportDescription}
              onChange={(e) => setState(prev => ({ ...prev, reportDescription: e.target.value }))}
              className="w-full p-2 border rounded-md mb-4"
              placeholder={t("Please describe the issue")}
              rows={4}
            />
            <div className="flex justify-start gap-2">
              <button
                onClick={handleReportSubmit}
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                {t("Submit")}
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, showReportDialog: false, reportDescription: '' }))}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {state?.chatModel && token && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t("Start a Conversation")}</h3>
            <textarea
              className="w-full p-2 border rounded-md mb-4 outline-none"
              placeholder={t("Type your first message")}
              rows={4}
              onChange={(e) => setState(prev => ({ ...prev, chatMessage: e.target.value }))}
            />
            <div className="flex justify-start gap-2">
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                {t("Send")}
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, chatModel: false }))}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="relative z-50 mt-8 rounded-3xl top-[-25px] bg-white">
      <div className="flex justify-between items-center p-4 relative">
        <button
          onClick={toggleMenu}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <p className="text-base text-end">{sallerInfo?.username || t("N/A")}</p>
            <p className="text-base text-gray-600">{sallerInfo?.followers_number || 0} {t("Followers")}</p>
          </div>
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white font-medium">{sallerInfo?.username?.charAt(0) || t("A")}</span>
          </div>
        </div>

        {state.isMenuOpen && (
          <div className={`absolute top-16 ${i18nextLng === "ar" ? "right-2" : "left-2"} w-48 bg-white rounded-md shadow-lg z-50 py-1 border`}>
            <button
              onClick={toggleFollowStatus}
              disabled={state.followLoading}
              className={`w-full px-4 py-2 text-sm hover:bg-gray-100 ${i18nextLng === "ar" ? "text-right" : "text-left"}`}
            >
              {state.sallerStatus.followed ? t("Unfollow") : t("Follow")}
            </button>
            <button
              onClick={() => setState(prev => ({ ...prev, showBlockDialog: true, isMenuOpen: false }))}
              className={`w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 ${i18nextLng === "ar" ? "text-right" : "text-left"}`}
            >
              {state.sallerStatus.blocked ? t("Unblock") : t("Block")}
            </button>
            <button
              onClick={() => setState(prev => ({ ...prev, showReportDialog: true, isMenuOpen: false }))}
              className={`w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 ${i18nextLng === "ar" ? "text-right" : "text-left"}`}
            >
              {t("Report")}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-center mx-4">
        <button onClick={chatHandler} className="flex items-center gap-2 border border-green-500 text-gray-700 px-4 py-2 rounded-lg transition-colors">
          <MessageCircle className="w-4 h-4" />
          <span>{t("Chat")}</span>
        </button>
        <Link
          href={`tel:`}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Phone className="w-4 h-4" />
          {sallerInfo?.phone?.slice(1) || t("N/A")}
        </Link>
      </div>

      {renderActionDialogs()}
    </div>
  );
};

export default SellerSection;